import partition from 'lodash/fp/partition';

import type { Maybe } from '@bedc/core';
import type { types } from '@bedc/types';

// Constants
// ------------------------------------------------------------------------

/** Matches a `{placeholder}` and captures its key. Global: a permission may carry several. */
const PLACEHOLDER_PATTERN = /{([^{}]*)}/g;

// Methods
// ------------------------------------------------------------------------

function hasPermissionWithRegexp<T extends string>(rule: RegExp) {
	return (permissions: T[]): boolean => permissions.some((p) => p.match(rule));
}

const hasPlaceholderPermission = hasPermissionWithRegexp(/{.*}/);
const hasCountryPlaceholderPermission = hasPermissionWithRegexp(/{countryCode}/);
const hasFranchisePlaceholderPermission = hasPermissionWithRegexp(/{franchiseId}/);
const hasPrincipalPlaceholderPermission = hasPermissionWithRegexp(/{principalId}/);
const hasOrganizationPlaceholderPermission = hasPermissionWithRegexp(/{organizationId}/);
const hasCustomerPlaceholderPermission = hasPermissionWithRegexp(/{customerId}/);
const hasClientPlaceholderPermission = hasPermissionWithRegexp(/{clientId}/);

function separatePermissionWithRegexp(rule: RegExp) {
	return <T extends string>(
		permissions: T[]
	): {
		matching: T[];
		notMatching: T[];
	} => {
		const [matching, notMatching] = partition((p) => p.match(rule), permissions);

		return {
			matching,
			notMatching,
		};
	};
}

const separatePlaceholderPermissions = separatePermissionWithRegexp(/{.*}/);
const separateCountryPlaceholderPermissions = separatePermissionWithRegexp(/{countryCode}/);
const separateFranchisePlaceholderPermissions = separatePermissionWithRegexp(/{franchiseId}/);
const separatePrincipalPlaceholderPermissions = separatePermissionWithRegexp(/{principalId}/);
const separateOrganizationPlaceholderPermissions = separatePermissionWithRegexp(/{organizationId}/);
const separateCustomerPlaceholderPermissions = separatePermissionWithRegexp(/{customerId}/);
const separateClientPlaceholderPermissions = separatePermissionWithRegexp(/{clientId}/);

// Evaluation
// ------------------------------------------------------------------------

/** The placeholder keys a single permission carries, in order of appearance. */
const getPlaceholderKeys = (permission: string): string[] =>
	// An empty `{}` yields '', which resolves to no replacement and therefore denies.
	[...permission.matchAll(PLACEHOLDER_PATTERN)].map((match) => match[1] ?? '');

/**
 * Expand a permission into every concrete permission string it could resolve to.
 *
 * Returns `null` when the permission cannot be resolved at all, which always denies:
 *
 * - a replacement is `undefined` — the caller forgot it, or the placeholder is unknown.
 *   Warned about, because the typed `check()` signature should have prevented it.
 * - a replacement is `null` — explicitly "not applicable".
 * - a replacement is an empty array — no entity in scope, so nothing can be granted.
 *   Note this used to succeed vacuously under `all`.
 *
 * Several placeholders produce the cartesian product of their ids.
 */
const expandPermission = (
	permission: string,
	replacements: types.acs.permission.Replacements
): string[] | null => {
	const keys = getPlaceholderKeys(permission);

	if (!keys.length) {
		return [permission];
	}

	let expanded = [permission];

	for (const key of keys) {
		const value = replacements[key as types.acs.permission.PermissionPlaceholder];

		if (typeof value === 'undefined') {
			// Warn: either the caller omitted a replacement the permission needs, or the
			// permission carries a placeholder that is not a known replacement key.
			console.warn(`Missing ${key} when checking for placeholder permission ${permission}`);

			return null;
		}

		if (value === null) {
			return null;
		}

		const ids: readonly string[] = typeof value === 'string' ? [value] : value;

		if (!ids.length) {
			return null;
		}

		expanded = expanded.flatMap((p) => ids.map((id) => p.replace(`{${key}}`, id)));
	}

	return expanded;
};

/**
 * Returns whether a permission check is satisfied by the available permissions.
 *
 * The check states its own quantifier, and that quantifier governs both levels:
 *
 * - `{ all: [...] }` — every permission must be owned, and where a replacement supplies
 *   several ids, the permission must be owned for every one of them.
 * - `{ any: [...] }` — at least one permission must be owned, and one matching id is enough.
 *
 * A permission whose placeholders cannot be resolved is denied rather than skipped, so an
 * unresolvable check fails closed. An empty permission list denies for the same reason.
 *
 * @param ownedPermission List of available permissions.
 * @param requiredPermission The check to evaluate.
 * @param replacements Placeholder replacements. Required only when the check needs them.
 * @returns True if the check is satisfied.
 */
const check = <const C extends types.acs.permission.Check>(
	ownedPermission: Maybe<readonly string[]>,
	requiredPermission: C,
	...[replacements = {}]: types.acs.permission.ReplacementsParams<C>
): boolean => {
	if (!ownedPermission || !ownedPermission.length) {
		return false;
	}

	const matchAllCondition = 'all' in requiredPermission;
	const unprojectedRequiredPermissions: readonly string[] = matchAllCondition
		? requiredPermission.all
		: requiredPermission.any;

	// `[].every(...)` is vacuously true, so an empty `all` would grant everything. The
	// `AtLeastOne` in `Check` rules this out at compile time, but a runtime-computed check
	// reaches here through a widened type — asking for nothing must never grant anything.
	if (!unprojectedRequiredPermissions.length) {
		return false;
	}

	const isSatisfied = (permission: string): boolean => {
		const projectedRequiredPermissions = expandPermission(permission, replacements);

		if (projectedRequiredPermissions === null) {
			return false;
		}

		return matchAllCondition
			? projectedRequiredPermissions.every((p) => ownedPermission.includes(p))
			: projectedRequiredPermissions.some((p) => ownedPermission.includes(p));
	};

	return matchAllCondition
		? unprojectedRequiredPermissions.every(isSatisfied)
		: unprojectedRequiredPermissions.some(isSatisfied);
};

/**
 * Evaluate a check and report which permissions were not satisfied.
 *
 * Intended for error messages and debugging. `missing` lists every unsatisfied permission
 * regardless of the quantifier, so under `any` a granted check can still report misses.
 *
 * @param ownedPermission List of available permissions.
 * @param required The check to evaluate.
 * @param replacements Placeholder replacements.
 * @returns Whether the check passed, and the permissions that did not resolve.
 */
const explain = (
	ownedPermission: Maybe<readonly string[]>,
	required: types.acs.permission.Check,
	replacements: types.acs.permission.Replacements = {}
): { granted: boolean; missing: types.acs.permission.Type[] } => {
	const permissions = 'all' in required ? required.all : required.any;

	const missing = permissions.filter(
		(permission) => !permissionUtils.check(ownedPermission, { all: [permission] }, replacements)
	);

	return {
		granted: permissionUtils.check(ownedPermission, required, replacements),
		missing: [...missing],
	};
};

export const permissionUtils = {
	hasPlaceholderPermission,
	hasCountryPlaceholderPermission,
	hasFranchisePlaceholderPermission,
	hasPrincipalPlaceholderPermission,
	hasOrganizationPlaceholderPermission,
	hasCustomerPlaceholderPermission,
	hasClientPlaceholderPermission,
	//
	separatePlaceholderPermissions,
	separateCountryPlaceholderPermissions,
	separateFranchisePlaceholderPermissions,
	separatePrincipalPlaceholderPermissions,
	separateOrganizationPlaceholderPermissions,
	separateCustomerPlaceholderPermissions,
	separateClientPlaceholderPermissions,
	//
	check,
	explain,
};
